import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

const GREEN = "#4A9D3C";
const GREEN_HOVER = "#3D8432";
const GREEN_DEEP = "#2D5A1B";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      // First-time login → show the Welcome / quick-start tour. Tracked per-user
      // in localStorage so each teammate sees it once on their own device.
      const userId = res.user?.id;
      const seen = userId && localStorage.getItem(`welcomeSeen_${userId}`);
      nav(seen ? "/" : "/welcome");
    } else {
      setError(res.error || "Unable to sign in");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50" data-testid="login-page">
      {/* Left panel — logos */}
      <div
        className="hidden md:flex md:w-5/12 lg:w-1/2 p-10 lg:p-14 flex-col items-center justify-center gap-10"
        style={{ background: `linear-gradient(160deg, ${GREEN_DEEP} 0%, ${GREEN} 100%)` }}
      >
        <div className="w-full max-w-sm bg-white p-8 rounded-2xl shadow-2xl flex items-center justify-center">
          <img
            src="/todds-nursery-logo.png"
            alt="Todd's Nursery & Landscape Installation"
            className="w-full h-auto max-h-56 object-contain"
          />
        </div>
        <div className="w-full max-w-sm bg-black p-8 rounded-2xl shadow-2xl flex items-center justify-center">
          <img
            src="/north-star-logo.png"
            alt="North Star Grounds Care"
            className="w-full h-auto max-h-40 object-contain"
          />
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logos */}
          <div className="md:hidden mb-8 grid grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 flex items-center justify-center aspect-square">
              <img src="/todds-nursery-logo.png" alt="Todd's Nursery" className="w-full h-full object-contain" />
            </div>
            <div className="bg-black p-3 rounded-xl shadow-sm flex items-center justify-center aspect-square">
              <img src="/north-star-logo.png" alt="North Star Grounds Care" className="w-full h-full object-contain" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-7 sm:p-9">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Sign in</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="email">Email</label>
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-base focus:outline-none transition-all"
                  onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${GREEN}`)}
                  onBlur={(e) => (e.target.style.boxShadow = "")}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="password">Password</label>
                <input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-base focus:outline-none transition-all"
                  onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${GREEN}`)}
                  onBlur={(e) => (e.target.style.boxShadow = "")}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full py-3.5 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed group"
                style={{ background: loading ? GREEN_HOVER : GREEN }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = GREEN_HOVER)}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = GREEN)}
              >
                {loading ? "Signing in…" : "Sign in"}
                {!loading && <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
